/**
 * Logistics Store
 *
 * Mirrors the Invoicing store: wires the logistics state + handlers + invariants
 * into a CDSE store and exposes domain-named hooks. The connector catalog is NOT
 * owned here — it lives in the shared systems-integrations store. The
 * `selectLogisticsConnectors` selector below is a thin wrapper over the shared
 * `selectConnectorsByKind` selector, filtered to `kind: 'logistics'`, so the
 * logistics subdomain never reimplements the catalog.
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { selectors as sharedSelectors } from '../../shared/state/store';
import type { ConnectorEntry, SystemsIntegrationsState } from '../../shared/state/state';
import { initialState, selectors } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { LogisticsCommand } from './commands';
import {
  createLogisticsListLoadPolicy,
  createLogisticsSubmitPolicy,
  createLogisticsDeletePolicy
} from './policies';

const store = createStore({
  name: 'logistics',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useLogisticsSelector,
  useErrors: useLogisticsErrors,
  usePolicies: useLogisticsPolicies,
  getState: getLogisticsState,
  dispatch: dispatchLogisticsCommand
} = store;

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor.
 */
export const useLogisticsDispatch = (): Dispatch<LogisticsCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<LogisticsCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory - creates policies that require backend + shared-state access.
 *
 * Mirrors `createInvoicingPolicies`: the submit policy needs to read the
 * connector catalog (capabilities) from the SHARED systems-integrations store
 * to branch on `resolveSetupPanelKind`, so the caller supplies a `getConnectors`
 * accessor.
 *
 * @param backend - Client library instance for API calls
 * @param getConnectors - Reads the current connector catalog from shared state
 */
export const createLogisticsPolicies = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, ConnectorEntry>
) => [
  createLogisticsListLoadPolicy( backend ),
  createLogisticsSubmitPolicy( backend, getConnectors ),
  createLogisticsDeletePolicy( backend )
];

/**
 * Catalog connectors of `kind: 'logistics'`. Reads the SHARED
 * systems-integrations catalog via `selectConnectorsByKind` rather than
 * reimplementing the filter, mirroring how the invoicing subdomain pulls its
 * connectors from the shared store.
 */
export const selectLogisticsConnectors = ( state: SystemsIntegrationsState ) =>
  sharedSelectors.selectConnectorsByKind( state, 'logistics' );

export const {
  selectLogisticsIntegrations,
  selectAddModalOpen
} = selectors;

export { selectors };
